/**
 * Shared pure-function helpers for box status logic.
 *
 * Extracted from use-logistics-api.ts and app-context.tsx so that
 * the identical logic lives in one place.
 */

import { BoxStatusCode, STATUS_CODE_ORDER } from '../types/logistics';

// ─── Status order ─────────────────────────────────────────────────────────────

/** Returns the numeric sort order for a BoxStatusCode (-1 if unknown). */
export function getStatusOrder(statusCode: BoxStatusCode): number {
  return STATUS_CODE_ORDER[statusCode] ?? -1;
}

// ─── Action → status code mapping ────────────────────────────────────────────

/** Maps a workflow action name (and optional location string) to the target BoxStatusCode. */
export function getStatusCodeForAction(action: string, location?: string): BoxStatusCode {
  switch (action) {
    case 'collect':            return 'ITKK';
    case 'fail-collection':    return 'WCFC';
    case 'warehouse-receive':  return 'WKKU';
    case 'warehouse-store':    return 'WKKS';
    case 'warehouse-pick':     return 'WKKP';
    case 'warehouse-hold-out': return 'WKKHO';
    case 'courier-load':       return 'ITTC';
    case 'deliver':            return 'WC';
    case 'fail-delivery':      return 'ITTKKFR';
    default:
      if (location) {
        if (location === 'Customer')                   return 'WC';
        if (location.includes('Courier-Incoming'))     return 'ITKK';
        if (location.includes('-IN'))                  return 'WKKU';
        if (location.includes('-St'))                  return 'WKKS';
        if (location.includes('-OUT'))                 return 'WKKHO';
        if (location.includes('Courier-Outgoing'))     return 'ITTC';
      }
      return 'WCAC';
  }
}

// ─── Progress calculation ─────────────────────────────────────────────────────

type ProgressBox = {
  statusCode: BoxStatusCode;
  isCompleted?: boolean;
};

type ProgressData = {
  boxes?: ProgressBox[];
  type?: string;
};

/**
 * Counts how many boxes have been completed for the given workflow `type`.
 * Uses `isCompleted` flag when present; falls back to statusCode order.
 */
export function calcProgress(type: string, data: ProgressData): number {
  let progress = 0;
  const boxes = data.boxes ?? [];

  for (const box of boxes) {
    if (box.isCompleted !== undefined) {
      if (box.isCompleted) progress++;
      continue;
    }

    const statusOrder = STATUS_CODE_ORDER[box.statusCode] ?? -1;

    if (type === 'order') {
      if (data.type === 'collection' && statusOrder >= 1) progress++;
      if (data.type === 'delivery'   && statusOrder === 6) progress++;
    } else {
      if (type === 'warehouse-unload' && (statusOrder === 2 || statusOrder === 3)) progress++;
      if (type === 'warehouse-store'  && statusOrder === 3)          progress++;
      if (type === 'warehouse-pick'   && statusOrder >= 4)           progress++;
      if (type === 'warehouse-load'   && statusOrder >= 5)           progress++;
    }
  }

  return progress;
}
